import styled, { css } from 'styled-components';
import { IconGlyph } from '@/app/common/icons/MaskIcon';
import { BASE_IMAGE_WIDTH, LARGE_IMAGE_WIDTH } from '@/app/components/search/components/Card/constants.ts';
import type { Localization } from '../../../../common/Localization.ts';

const MARGIN = '16px';
const CONTAINER_WIDTH = '28px';

const buttonStyles = css`
  background-color: transparent;
  width: ${CONTAINER_WIDTH};
  height: ${CONTAINER_WIDTH};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  cursor: pointer;
  animation: fadeInFromNone 0.5s ease-out;

  img {
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    user-select: none;
  }
`;

const buttonPosition = css`
  position: absolute;
  z-index: 20;
  top: ${MARGIN};

  left: calc(100% - ${MARGIN} - ${CONTAINER_WIDTH});

  @media (min-width: 768px) {
    left: calc(${BASE_IMAGE_WIDTH} - ${MARGIN} - ${CONTAINER_WIDTH});
  }

  @media (min-width: 1440px) {
    left: calc(${LARGE_IMAGE_WIDTH} - ${MARGIN} - ${CONTAINER_WIDTH});
  }
`;

const selectedStyles = css<{ localization: Localization }>`
  cursor: pointer;

  &:after {
    background: white;
    opacity: 1;
  }
`;

const buttonBackground = css`
  &:after {
    content: '';
    background: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    border-radius: 50%;
    opacity: 0.5;
  }

  &:hover:after {
    opacity: 1;
  }
`;

const promoButtonBackground = css`
  ${buttonBackground};

  &:after {
    animation: comparisonGradientFromLeftToRight 1.6s ease-in-out infinite;
    background: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
    background-image: linear-gradient(
      90deg,
      ${({ theme }) => theme.kallisto.color.text.static.textPrimary} 0%,
      ${({ theme }) => theme.kallisto.color.text.static.textPrimary} 50%,
      rgba(255, 255, 255, 0.5) 100%
    );
    background-size: 300% 100%;
  }
`;

const tooltipOverrides = css`
  [role='tooltip'] {
    .MuiTooltip-tooltip {
      background-color: white;
      box-shadow: ${({ theme }): string => theme?.kallisto?.shadow?.largeDownShadow};
      z-index: 0;

      > div {
        padding: 0;

        > div {
          color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

          @media (min-width: 768px) {
            white-space: nowrap;
          }
        }
      }
      .MuiTooltip-arrow {
        color: white;
      }
    }
  }
`;

const promoStyles = css`
  ${IconGlyph} {
    position: absolute;
    top: 14px;
    right: 18px;
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  }

  h2 {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }

  p {
    white-space: normal;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
`;

export const AddToComparisonContainer = styled.button<{
  isSelected: boolean;
  isPromo?: boolean;
  isPromoButton?: boolean;
  localization: Localization;
}>`
  ${buttonStyles};
  ${buttonPosition};
  ${({ isPromoButton }) => (isPromoButton ? promoButtonBackground : buttonBackground)};
  ${({ isSelected }) => isSelected && selectedStyles};
  ${({ isPromo }) => isPromo && promoStyles};
  ${tooltipOverrides};
`;
